import { gsap } from 'gsap'
import Experience from '../Experience.js'

// Bascule entre la visite guidée (scroll), l'exploration libre
// (OrbitControls) et le plan du parcours (vue orthographique),
// disponible à tout moment de l'expérience.
export default class ModeToggle {
    constructor() {
        this.experience = new Experience()
        this.root = document.querySelector('.mode-toggle')
        this.buttons = this.root?.querySelectorAll('[data-mode]') ?? []
        this.hint = document.querySelector('.free-hint')
        this.canvas = this.experience.canvas
        this.hintDuration = 6000
        this.hintTimer = null
        this.transitioning = false

        this.bindEvents()
        this.sync()
    }

    get mode() {
        return this.experience.timeline?.viewMode ?? 'guided'
    }

    bindEvents() {
        this.onClick = (event) => {
            const button = event.target.closest('[data-mode]')
            if (button) this.setMode(button.dataset.mode)
        }
        this.root?.addEventListener('click', this.onClick)

        // Raccourcis : « L » exploration libre, « M » plan, Échap pour revenir.
        this.onKeyDown = (event) => {
            if (event.target.closest?.('input, textarea, select')) return
            const key = event.key.toLowerCase()
            if (key === 'l') {
                this.setMode(this.mode === 'free' ? 'guided' : 'free')
            } else if (key === 'm') {
                this.setMode(this.mode === 'map' ? 'guided' : 'map')
            } else if (event.key === 'Escape' && this.mode !== 'guided') {
                this.setMode('guided')
            }
        }
        window.addEventListener('keydown', this.onKeyDown)
    }

    setMode(mode, { blend = true, onSwitched } = {}) {
        const timeline = this.experience.timeline
        if (!timeline || this.transitioning || mode === this.mode) return
        if (timeline.introState !== 'done') return

        // Entrée et sortie du plan : changement de caméra masqué par un
        // court fondu, la perspective ne peut pas s'interpoler en ortho.
        const fade = mode === 'map' || this.mode === 'map'

        const apply = () => {
            timeline.setViewMode(mode, { blend })
            onSwitched?.()
            this.sync()
            this.updateHint(mode)
        }

        if (!fade || !this.canvas) {
            apply()
            return
        }

        this.transitioning = true
        gsap.killTweensOf(this.canvas)
        gsap.to(this.canvas, {
            opacity: 0,
            duration: 0.28,
            ease: 'power2.in',
            onComplete: () => {
                apply()
                gsap.to(this.canvas, {
                    opacity: 1,
                    duration: 0.55,
                    ease: 'power2.out',
                    onComplete: () => { this.transitioning = false }
                })
            }
        })
    }

    // Depuis le plan : la visite guidée reprend directement au départ du trou.
    goToHole(hole) {
        const progress = this.experience.timeline?.getHoleEntryProgress(hole)
        if (progress == null) return

        this.setMode('guided', {
            blend: false,
            onSwitched: () => this.experience.timeline.jumpTo(progress)
        })
    }

    sync() {
        const mode = this.mode
        if (this.root) this.root.dataset.mode = mode

        for (const button of this.buttons) {
            const active = button.dataset.mode === mode
            button.classList.toggle('is-active', active)
            button.setAttribute('aria-pressed', String(active))
        }
    }

    getHintCopy(mode) {
        const mobile = this.experience.isMobile

        if (mode === 'free') {
            return mobile
                ? 'Un doigt pour pivoter · deux doigts pour zoomer et vous déplacer'
                : 'Glisser pour pivoter · clic droit pour vous déplacer · molette pour zoomer'
        }

        if (mode === 'map') {
            return mobile
                ? 'Touchez un numéro pour rejoindre ce trou · deux doigts pour incliner'
                : 'Cliquez sur un numéro pour rejoindre ce trou · clic droit pour incliner · molette pour zoomer'
        }

        return ''
    }

    updateHint(mode) {
        const copy = this.getHintCopy(mode)
        if (copy) this.showHint(copy)
        else this.hideHint()
    }

    showHint(copy) {
        if (!this.hint) return

        window.clearTimeout(this.hintTimer)
        this.hint.textContent = copy
        this.hint.classList.add('is-visible')
        this.hintTimer = window.setTimeout(() => this.hideHint(), this.hintDuration)
    }

    hideHint() {
        window.clearTimeout(this.hintTimer)
        this.hintTimer = null
        this.hint?.classList.remove('is-visible')
    }

    destroy() {
        this.hideHint()
        gsap.killTweensOf(this.canvas)
        this.root?.removeEventListener('click', this.onClick)
        window.removeEventListener('keydown', this.onKeyDown)

        this.root = null
        this.buttons = []
        this.hint = null
        this.experience = null
    }
}
